/**
 * Utility functions for transforming merchant profile data
 * Used in UserPublicProfile to convert API response to component props
 */

import { extractValidAddress, formatAddressForDisplay } from './profileHelpers';

/**
 * Transform merchant profile from API response to MerchantProfilePreview props
 * 
 * @param merchantProfile - Merchant profile from API response
 * @param username - Fallback username if merchant name not available
 * @returns Transformed data for MerchantProfilePreview component
 */
export const transformMerchantProfileForPreview = (
  merchantProfile: any,
  username: string
): {
  name: string;
  description?: string;
  address?: string;
  phone?: string;
  website?: string;
  email?: string;
  hours?: Array<{ day: string; text: string }>;
  rating?: number;
  reviewCount?: number;
  photos?: string[];
  logo?: string;
} | undefined => {
  if (!merchantProfile) return undefined;

  // Transform address
  const address = merchantProfile.contact?.address || merchantProfile.location?.components;
  let formattedAddress: string | undefined;
  
  if (address) {
    const validAddress = extractValidAddress(address);
    if (validAddress) {
      formattedAddress = formatAddressForDisplay(validAddress) || undefined;
    }
  }

  // Transform business hours
  const hours = merchantProfile.business?.hours 
    ? Object.entries(merchantProfile.business.hours).map(([day, hours]: [string, any]) => ({
        day,
        text: hours ? `${hours.open} - ${hours.close}` : 'Closed'
      }))
    : undefined;

  // Transform photos
  const photos = merchantProfile.media?.images?.map((img: any) => img.url) || 
                 merchantProfile.photos || 
                 [];

  return {
    name: merchantProfile.name || merchantProfile.businessName || username,
    description: merchantProfile.description,
    address: formattedAddress,
    phone: merchantProfile.contact?.phone || merchantProfile.contact?.mobile || merchantProfile.phone,
    website: merchantProfile.contact?.website || merchantProfile.website,
    email: merchantProfile.contact?.email || merchantProfile.email,
    hours,
    rating: merchantProfile.business?.rating,
    reviewCount: merchantProfile.business?.reviewCount,
    photos,
    logo: merchantProfile.media?.logo || merchantProfile.logo
  };
};

/**
 * Transform merchant profile social media data
 * 
 * @param merchantProfile - Merchant profile from API response
 * @returns Social media links object
 */
export const transformMerchantSocialMedia = (merchantProfile: any): {
  youtube?: string;
  instagram?: string;
  facebook?: string;
  tiktok?: string;
  twitter?: string;
  linkedin?: string;
  wordpress?: string;
} | undefined => {
  if (!merchantProfile?.social) return undefined;

  return {
    youtube: merchantProfile.social.youtube,
    instagram: merchantProfile.social.instagram,
    facebook: merchantProfile.social.facebook,
    tiktok: merchantProfile.social.tiktok,
    twitter: merchantProfile.social.twitter,
    linkedin: merchantProfile.social.linkedin,
    wordpress: merchantProfile.social.wordpress
  };
};



